import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { forwardRef, type ReactNode } from 'react';

import { CheckIcon, type IconSize } from '@air/design-icons';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const checkbox = tailwindVariants({
  base: 'rounded-2 border border-gray-8 radix-state-checked:ads:border-primary radix-state-checked:border-blue-9 ads:bg-base bg-gray-1 radix-state-checked:ads:bg-primary radix-state-checked:bg-blue-9 radix-state-checked:text-white hover:radix-state-checked:ads:bg-hover-primary hover:radix-state-checked:bg-blue-10 disabled:border-gray-5 disabled:radix-state-checked:ads:border-ads-gray-7 disabled:radix-state-checked:border-gray-7 disabled:ads:bg-raised disabled:bg-gray-2 disabled:radix-state-checked:ads:bg-ads-gray-7 disabled:radix-state-checked:bg-gray-7',
  defaultVariants: {
    size: 'medium',
  },
  variants: {
    size: {
      'extra-large': 'h-6 w-6',
      large: 'h-5 w-5',
      medium: 'h-4 w-4',
      small: 'h-3 w-3',
    },
  },
});

export type CheckboxVariants = VariantProps<typeof checkbox>;

// checkbox's size classes expressed as ADS size tokens: h-3 -> xs, h-4 -> sm, h-5 -> md, h-6 -> lg
const CHECKBOX_ICON_SIZE: Record<NonNullable<CheckboxVariants['size']>, IconSize> = {
  'extra-large': 'lg',
  large: 'md',
  medium: 'sm',
  small: 'xs',
};

export type CheckboxProps = RadixCheckbox.CheckboxProps &
  CheckboxVariants & {
    renderIndicator?: (props: { checked: RadixCheckbox.CheckboxProps['checked'] }) => ReactNode;
  };

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ checked, className, size, renderIndicator, ...restOfProps }, forwardedRef) => {
    return (
      <RadixCheckbox.Root
        checked={checked}
        className={checkbox({ class: className, size })}
        ref={forwardedRef}
        {...restOfProps}
      >
        <RadixCheckbox.Indicator className="flex items-center justify-center">
          {renderIndicator ? renderIndicator({ checked }) : <CheckIcon size={CHECKBOX_ICON_SIZE[size ?? 'medium']} />}
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
    );
  },
);

Checkbox.displayName = 'Checkbox';
